import { styled, Theme } from 'baseui'
import { FlexBetween, FlexCenterY, FlexColumn } from '@/shared/theme/styled-components'

export const StyledContainer = styled<'div', { $mastered?: boolean }>('div', ({ $theme, $mastered }) => ({
  width: '100%',
  border: `0.1rem solid ${$mastered ? $theme.colors.warning400 : 'transparent'}`,
  borderRadius: $theme.borders.radius300,
}))

export const StyledTopContainer = styled('div', () => ({
  width: '100%',
  padding: '1rem',
}))

export const StyledHeader = styled(FlexBetween, {
  marginBottom: '0.5rem',
})

export const StyledTitle = styled('h3', ({ $theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  fontWeight: '600',
  fontSize: '1.125rem',
  lineHeight: '160%',
  color: $theme.colors.textHeading,
}))

export const StyledIndicator = styled('span', ({ $theme }) => ({
  fontWeight: '600',
  fontSize: '1.125rem',
  lineHeight: '160%',
  color: $theme.colors.textHeading,
}))

export const StyledCurrentValue = styled<'span', { $reachedMax?: boolean }>('span', ({ $theme, $reachedMax }) => ({
  color: $reachedMax ? $theme.colors.positive600 : $theme.colors.mono500,
}))

export const StyledSubtitle = styled('p', ({ $theme }) => ({
  margin: '0 0 0.75rem 0',
  fontSize: '0.85rem',
  color: $theme.colors.textSecondaryAlt,
}))

export const StyledProgressBarContainer = styled('div', () => ({
  width: '100%',
}))

export const StyledProgressTrack = styled<'div', { $mastered?: boolean }>('div', ({ $theme, $mastered }) => ({
  position: 'relative',
  width: '100%',
  height: $theme.sizing.scale400,
  borderRadius: '0.5rem',
  overflow: 'hidden',
  background: $mastered ? $theme.colors.positive500 : 'linear-gradient(90deg, #FFA051 0%, #FFD152 55%, #25A337 100%)',
}))

export const StyledProgressMask = styled<'div', { $percentDone: number }>('div', ({ $percentDone }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: `${100 - $percentDone}%`,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  transition: 'width 0.3s ease',
}))

export const StyledItemsContainer = styled('div', ({ $theme }) => ({
  borderTop: `0.1rem solid ${$theme.colors.borderSoft}`,
}))

export const StyledItemRow = styled<'div', { $done?: boolean }>('div', ({ $theme, $done }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 1rem',
  backgroundColor: $done ? $theme.colors.positive50 : 'transparent',
}))

export const StyledItemLeft = styled(FlexCenterY, {
  gap: '0.5rem',
})

export const StyledItemLabel = styled<'span', { $done?: boolean }>('span', ({ $theme, $done }) => ({
  color: $done ? $theme.colors.positive600 : $theme.colors.textHeading,
}))

export const StyledDoneIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.positive500,
}))

export const StyledPendingIcon = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  color: $theme.colors.mono500,
}))

export const StyledActions = styled(FlexColumn, {
  width: '100%',
  gap: '0.5rem',
})

export const StyledCardHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  marginBottom: '0.5rem',
})

export const StyledCardTitle = styled('strong', ({ $theme }) => ({
  fontSize: '0.95rem',
  color: $theme.colors.textHeading,
}))

export const StyledBadge = styled<'span', { $muted?: boolean }>('span', ({ $theme, $muted }) => ({
  flex: '0 0 auto',
  padding: '0.15rem 0.5rem',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: $muted ? $theme.colors.textMuted : $theme.colors.white,
  backgroundColor: $muted ? $theme.colors.borderLight : $theme.colors.warning400,
}))

export const StyledSteps = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  margin: '0 0 0.75rem',
})

export const StyledStep = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
})

export const StyledStepMarker = styled<'span', { $done?: boolean; $current?: boolean }>(
  'span',
  ({ $theme, $done, $current }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.15rem',
    height: '1.15rem',
    borderRadius: '50%',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: $done || $current ? $theme.colors.white : $theme.colors.textMuted,
    backgroundColor: $done ? $theme.colors.positive500 : $current ? $theme.colors.primary : $theme.colors.borderLight,
  }),
)

export const StyledStepLabel = styled<'span', { $done?: boolean; $current?: boolean }>(
  'span',
  ({ $theme, $done, $current }) => ({
    fontSize: '0.75rem',
    fontWeight: $current ? 700 : 400,
    color: $done || $current ? $theme.colors.textHeading : $theme.colors.textMuted,
  }),
)

export const StyledStepConnector = styled<'span', { $done?: boolean }>('span', ({ $theme, $done }) => ({
  flex: '1 1 auto',
  height: '1px',
  backgroundColor: $done ? $theme.colors.positive500 : $theme.colors.borderLight,
}))

export const StyledCodeBox = styled('div', ({ $theme }) => ({
  padding: '0.75rem',
  textAlign: 'center',
  borderRadius: '8px',
  backgroundColor: $theme.colors.bgSubtle,
  border: `1px solid ${$theme.colors.borderLight}`,
}))

export const StyledCode = styled('div', ({ $theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  letterSpacing: '0.2em',
  color: $theme.colors.primary,
}))

export const StyledCodeExpiry = styled('p', ({ $theme }) => ({
  margin: '0.25rem 0 0.5rem',
  fontSize: '0.8rem',
  color: $theme.colors.textMuted,
}))

const PILL_RADIUS = {
  borderTopLeftRadius: '999px',
  borderTopRightRadius: '999px',
  borderBottomLeftRadius: '999px',
  borderBottomRightRadius: '999px',
}

// baseui rounds the bar with the four longhand radii and slides the fill with a transform, so a
// `borderRadius` shorthand loses the merge and a transition on `width` animates nothing.
export const CodeProgressOverrides = {
  BarContainer: {
    style: () => ({ width: '100%', marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 }),
  },
  Bar: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...PILL_RADIUS,
      width: '100%',
      height: '0.3rem',
      backgroundColor: $theme.colors.borderLight,
    }),
  },
  BarProgress: {
    style: ({ $theme }: { $theme: Theme }) => ({
      ...PILL_RADIUS,
      backgroundColor: $theme.colors.primary,
      transition: 'transform 1s linear',
    }),
  },
}

export const StyledOrDivider = styled('div', ({ $theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  margin: '0.75rem 0',
  fontSize: '0.8rem',
  color: $theme.colors.textMuted,
  '::before': { content: '""', flex: '1 1 auto', height: '1px', backgroundColor: $theme.colors.borderLight },
  '::after': { content: '""', flex: '1 1 auto', height: '1px', backgroundColor: $theme.colors.borderLight },
}))

export const StyledInfo = styled('p', ({ $theme }) => ({
  margin: '0.5rem 0 0',
  fontSize: '0.85rem',
  lineHeight: 1.45,
  color: $theme.colors.textSecondaryAlt,
}))

export const StyledCodeCard = styled<'div', { $muted?: boolean }>('div', ({ $theme, $muted }) => ({
  width: '100%',
  marginBottom: '0.5rem',
  padding: '0.5rem',
  borderRadius: $theme.borders.radius200,
  border: `1px dashed ${$muted ? $theme.colors.borderLight : $theme.colors.primary400}`,
  backgroundColor: $muted ? 'transparent' : $theme.colors.bg050,
  color: $theme.colors.grey,
  ...$theme.typography.ParagraphXSmall,
}))

export const StyledScript = styled('p', {
  margin: '0 0 0.5rem',
})

export const StyledSentBox = styled('div', ({ $theme }) => ({
  margin: '0 0 0.5rem',
  padding: '0.5rem',
  borderRadius: $theme.borders.radius200,
  backgroundColor: $theme.colors.successBg,
}))

export const StyledSentTitle = styled('div', {
  fontWeight: 700,
})

export const StyledSentDetails = styled('div', ({ $theme }) => ({
  color: $theme.colors.textSecondaryAlt,
}))
